import React from 'react';
import * as MUI from '@mui/material';
import CommentModerationCard from '../components/moderation/CommentModerationCard';
import MinimalAlert from '../components/moderation/common/MinimalAlert';
import useCommentModeration from '../hooks/useCommentModeration';

const CommentModeration = () => {
  const {
    tabIndex,
    currentType,
    comments,
    thresholds,
    pendingActionId,
    snackbar,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isError,
    error,
    isMutating,
    hasNextPage,
    fetchNextPage,
    handleTabChange,
    handleApprove,
    handleReject,
    refetch,
    closeSnackbar,
    commentTypes,
  } = useCommentModeration();

  return (
    <>
      <MUI.Stack spacing={4}>
        <MUI.Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <MUI.Typography variant="title_text">
            Comment Moderation
          </MUI.Typography>
          <MUI.Button
            variant="contained"
            onClick={() => refetch()}
            disabled={isLoading || isFetching}
            sx={{
              backgroundColor: '#000000',
              color: '#ffffff',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#1a1a1a',
              },
            }}
          >
            {isFetching ? 'Refreshing...' : 'Refresh'}
          </MUI.Button>
        </MUI.Stack>
        <MUI.Divider />

        <MUI.Tabs
          value={tabIndex}
          onChange={handleTabChange}
          aria-label="moderation tabs"
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: '#000000',
            },
            '& .MuiTab-root': {
              color: '#878787',
              '&.Mui-selected': {
                color: '#000000',
                fontWeight: 'bold',
              },
            },
          }}
        >
          <MUI.Tab label="Skin Comments" />
          <MUI.Tab label="Champion Comments" />
          <MUI.Tab label="Post Comments" />
        </MUI.Tabs>

        {isError && (
          <MUI.Alert severity="error">
            {error?.response?.data?.error ||
              error?.message ||
              'Failed to load moderation queue.'}
          </MUI.Alert>
        )}

        {isLoading ? (
          <MUI.Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            minHeight={240}
          >
            <MUI.CircularProgress />
          </MUI.Box>
        ) : (
          <MUI.Stack spacing={3}>
            {comments.length === 0 && !isFetching && (
              <MinimalAlert severity="info">
                All caught up! There are no {currentType} comments waiting for review.
              </MinimalAlert>
            )}

            {comments.map((comment) => (
              <CommentModerationCard
                key={comment.commentId}
                comment={comment}
                subjectType={currentType}
                skin={currentType === 'skin' ? comment.skin : undefined}
                champion={currentType === 'champion' ? comment.champion : undefined}
                post={currentType === 'post' ? comment.post : undefined}
                thresholds={thresholds}
                onApprove={() => handleApprove(comment.commentId)}
                onReject={() => handleReject(comment.commentId)}
                isProcessing={
                  isMutating && pendingActionId === comment.commentId
                }
              />
            ))}

            {hasNextPage && (
              <MUI.Box display="flex" justifyContent="center">
                <MUI.Button
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  variant="outlined"
                >
                  {isFetchingNextPage ? 'Loading...' : 'Load more'}
                </MUI.Button>
              </MUI.Box>
            )}
          </MUI.Stack>
        )}
      </MUI.Stack>

      <MUI.Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MUI.Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </MUI.Alert>
      </MUI.Snackbar>
    </>
  );
};

export default CommentModeration;
